import { useState } from "react";
import { Check, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useGoals } from "@/lib/workspace";
import { errorMessage } from "@/lib/auth";

export const TrainingGoals = () => {
  const { goals, completed, total, loading, error, reload, add, toggle, remove } = useGoals();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async e => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      await add(title.trim());
      setTitle(""); setOpen(false);
    } catch (err) {
      toast.error(errorMessage(err, "Impossible d'ajouter cet objectif."));
    } finally { setBusy(false); }
  };

  const onToggle = async goal => {
    try { await toggle(goal); } catch (err) { toast.error(errorMessage(err)); }
  };

  const onRemove = async goal => {
    try { await remove(goal.id); } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <section className="training-goals">
      <div className="section-heading">
        <h2>Le programme du jour</h2>
        <span className="goal-count" data-testid="goals-progress">{completed}/{total}</span>
      </div>
      <div className="goal-progress" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={completed} data-testid="goals-progress-bar">
        <span style={{ width: `${total ? (completed / total) * 100 : 0}%` }} />
      </div>

      <div className="goals-list">
        {loading && <div className="empty-state" data-testid="goals-loading"><p>Chargement…</p></div>}
        {!loading && error && (
          <div className="empty-state" data-testid="goals-error">
            <p>Vos objectifs n'ont pas pu être chargés.</p>
            <button type="button" className="add-goal" onClick={reload}>Réessayer</button>
          </div>
        )}
        {!loading && !error && goals.length === 0 && (
          <div className="empty-state" data-testid="goals-empty"><p>Aucun objectif. Ajoutez-en un ci-dessous.</p></div>
        )}
        {goals.map(g => (
          <div key={g.id} className={`goal-item ${g.done ? "is-complete" : ""}`}>
            <label>
              <input type="checkbox" checked={g.done} data-testid={`goal-toggle-${g.id}`} onChange={() => onToggle(g)} />
              <span className="goal-check">{g.done && <Check size={11} />}</span>
              <span><strong>{g.title}</strong></span>
            </label>
            <button type="button" className="goal-remove" aria-label={`Retirer « ${g.title} »`} data-testid={`goal-remove-${g.id}`} onClick={() => onRemove(g)}>
              <X size={13} />
            </button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button data-testid="add-goal-button" className="add-goal"><Plus size={14} />Ajouter un objectif</button>
        </DialogTrigger>
        <DialogContent data-testid="add-goal-modal">
          <DialogTitle>Votre prochain objectif</DialogTitle>
          <DialogDescription>Un objectif concret pour votre prochaine séance.</DialogDescription>
          <form onSubmit={submit}>
            <label htmlFor="goal-title">Objectif</label>
            <Input id="goal-title" data-testid="goal-title-input" value={title} onChange={e => setTitle(e.target.value)} maxLength={160} required placeholder="Ex. Travailler mes déplacements" className="my-3" />
            <Button data-testid="goal-save-button" type="submit" disabled={busy}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : null}Ajouter l'objectif
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};
